"use client";

import { ProfileProps } from "../profile.types";
import { useEffect, useMemo, useState } from "react";

import { signIn, useSession, signOut } from "next-auth/react";

import Account from "@/components/profile/account";
import { BusinessDetails } from "@/components/profile/business_details";
import ReviewForm from "@/components/profile/form/review";
import { ManageVideos } from "@/components/profile/manage-uploads";
import Transactions from "@/components/profile/meeting_transactions";
import { SkillCategories } from "@/components/profile/skill_categories";
import { User } from "@/components/profile/user";
import ReviewSlide from "@/components/profile/view/review";

import { useRouter, useSearchParams } from "next/navigation";

import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from "@/components/ui/dialog";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { useToast } from "@/components/ui/use-toast";
import { MixpanelTracking } from "@/lib/mixpanel-browser-analytics";
import { debugLog } from "@/services/logging";
import { Badge } from "@/components/ui/badge";
import { UserStatus } from "@/models/user-status";
import { AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DialogClose } from "@radix-ui/react-dialog";
import { isMember } from "@/lib/api/authentication";
import { revalidateTag } from "next/cache";

//Precondtion: The logged in user's id must make the
export default function Profile(props: ProfileProps) {
	const params = useSearchParams();
	const [isLoading, setIsLoading] = useState(false);
	const [showReviewModal, setShowReviewModal] = useState(false);
	const [canReview, setCanReview] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [reviews, setReviews] = useState(props.userReviews);
	const [avgRating, setAvgRating] = useState(props.averageRating);
	//TODO Re-evaluate the use of state objects to store variables
	const [isUserAdmin, setIsUserAdmin] = useState(props.isAdmin);
	const [profile, setProfile] = useState(props.profile);
	const { toast } = useToast();
	const router = useRouter();

	const { data: session } = useSession();
	const onCreateReview = async (review: any) => {
		const oldReviews = Array.from(reviews);

		const res = await fetch("/api/review", {
			method: "POST",
			body: JSON.stringify({
				userId: profile?.userId,
				review: review,
			}),
		});
		const result = await res.json();
		if (result) {
			toast({ title: "Review Posted", duration: 3000 });
			MixpanelTracking.getInstance().userReviewCreated({
				reviewer_id: session?.user.id ?? "",
				profile_user_id: profile.userId,
				rating: review?.rating ?? "",
				action: "Review created on user profile",
			});
			oldReviews.push(result);
			setReviews([...reviews, result]);
			calculateAvgRating(oldReviews);
		}
	};

	const onUpdateUsername = (firstName: string, lastName: string) => {
		setProfile((profile) => ({
			...profile,
			firstName: firstName,
			lastName: lastName,
		}));
		window.location.reload();
	};

	const calculateAvgRating = (reviews: {}[]) => {
		let sum = 0;
		reviews.forEach(function (num: any) {
			sum += num.rating;
		});
		const average = (sum / reviews.length).toFixed(1);
		if (average !== "NaN") {
			setAvgRating(average);
		}

		return "0";
	};

	const onUpdateReview = async (review: any) => {
		const updatedReviews = reviews.map((r: any) => {
			if (r.id !== review.id) {
				return r;
			}

			return {
				...r,
				reply: review.reply,
			};
		});

		setReviews(updatedReviews);
	};
	useEffect(() => {
		setProfile(props.profile);
		if (params.get("tab") === "meeting") {
			setTabChange("Meetings");
		}
	}, [props.profile]);
	//TODO Dissect into server and client component
	// Content is a component

	const Content = () => {
		const ProfileTabs = () => {
			return (
				<Tabs
					value={`${active}`}
					onValueChange={(s) => setTabChange(s)}
					className="w-full sm:w-4/5 justify-stretch mt-2 sm:mt-0  sm:m-auto">
					<TabsList
						className={` w-full  overflow-x-auto h-20 justify-start sm:justify-center`}>
						{types.map((type) => {
							return (
								<TabsTrigger
									value={`${type}`}
									className="flex-grow flex-shrink-0"
									key={type}>
									{type}
									{type === "Meetings" &&
										profile.status == UserStatus.PAYMENT && (
											<AlertCircle
												className="ml-2"
												color="#ff0000"
											/>
										)}
								</TabsTrigger>
							);
						})}
					</TabsList>
					{types.map((type) => {
						return (
							<TabsContent
								className="px-10 py-12 sm:px-0 "
								value={type}
								key={type}>
								{typesContentMap.get(type)}{" "}
							</TabsContent>
						);
					})}
				</Tabs>
			);
		};

		if (props.isTeacher) {
			return (
				<>
					<div className="bg-gray-50">
						<User
							id={profile?.userId}
							lastName={profile?.lastName}
							firstName={profile?.firstName}
							email={profile?.email}
							pic={profile?.picLocation}
							tier={profile?.tier}
							created={profile?.createdAt}
							avgRating={avgRating}
							setIsLoading={setIsLoading}
							setCanReview={setCanReview}
							setShowModal={setShowModal}
							setShowReviewModal={setShowReviewModal}
							isMember={props.isMember}
							isAdmin={props.isAdmin}
							isTeacher={props.isTeacher}
						/>

						<ProfileTabs />
					</div>
				</>
			);
		} else if (profile?.tier?.type == "Promoter") {
			debugLog("---Hey is Promoter ");
			return (
				<>
					<div className="bg-gray-50">
						<User
							id={profile?.userId}
							lastName={profile?.lastName}
							firstName={profile?.firstName}
							email={profile?.email}
							pic={profile?.picLocation}
							tier={profile?.tier}
							created={profile?.createdAt}
							avgRating={avgRating}
							setIsLoading={setIsLoading}
							setCanReview={setCanReview}
							setShowModal={setShowModal}
							setShowReviewModal={setShowReviewModal}
							isMember={props.isMember}
							isAdmin={props.isAdmin}
						/>

						<ProfileTabs />
					</div>
				</>
			);
		} else {
			debugLog("--Something else");
			return (
				<>
					<div className="bg-gray-50 ">
						<User
							id={profile?.userId}
							lastName={profile?.lastName}
							firstName={profile?.firstName}
							email={profile?.email}
							pic={profile?.picLocation}
							tier={profile?.tier}
							created={profile?.createdAt}
							setIsLoading={setIsLoading}
							avgRating={avgRating}
							setCanReview={setCanReview}
							setShowModal={setShowModal}
							setShowReviewModal={setShowReviewModal}
							isMember={props.isMember}
							isAdmin={props.isAdmin}
						/>
					</div>
				</>
			);
		}
	};

	const contentMap = () => {
		const types = props.isTeacher
			? props.isMember || isUserAdmin
				? [
						"Account",
						"Business Details",
						"Skill Categories",
						"Videos",
						"Meetings",
				  ]
				: ["Business Details", "Skill Categories", "Videos"]
			: props.isMember || isUserAdmin
			? ["Account", "Business Details", "Videos", "Meetings"]
			: ["Business Details", "Videos"];

		const typesContentMap = new Map<string, React.JSX.Element>();
		typesContentMap.set(
			"Account",
			<Account
				lastName={profile?.lastName}
				firstName={profile?.firstName}
				userId={profile?.userId}
				onUpdateName={onUpdateUsername}
			/>
		);
		typesContentMap.set(
			"Business Details",
			<BusinessDetails
				userId={profile?.userId}
				isMember={props.isMember}
				isAdmin={props.isAdmin}
			/>
		);
		typesContentMap.set(
			"Skill Categories",
			<SkillCategories
				userId={profile?.userId}
				tier={profile?.tier?.type!}
				isMember={props.isMember}
				isAdmin={props.isAdmin}
			/>
		);
		typesContentMap.set(
			"Videos",
			<ManageVideos
				userId={profile?.userId}
				tier={profile?.tier?.type!}
				isMember={props.isMember}
				isAdmin={props.isAdmin}
			/>
		);
		typesContentMap.set(
			"Meetings",
			<Transactions userId={profile?.userId} />
		);

		return { types, typesContentMap };
	};

	const o = contentMap();

	const types = o.types;

	const typesContentMap = o.typesContentMap;
	// debugLog("Profile props in profile page", profile)
	// debugLog("Original ", props.profile)
	// return (<><h1>Hello World2 </h1></>)
	const x = Content();
	const [TabContent, setTabContent] = useState(x);

	const [active, setActive] = useState(
		props.isMember || isUserAdmin ? types[1] : types[0]
	);

	const setTabChange = (s: string) => {
		debugLog("Tab changed to ", s);
		trackDetailsViewed(s);
		setActive(s);
	};

	useEffect(() => {
		const newx = Content();
		setTabContent(newx);
	}, [active]);

	const trackDetailsViewed = (tabTitle: string) => {
		MixpanelTracking.getInstance().profileDetailsTabClicked({
			tab_title: tabTitle,
			user_id: profile.userId,
			is_profile_owner: profile.userId === session?.user.id,
		});
	};

	return (
		<>
			{TabContent}

			{showReviewModal /*true*/ && (
				<>
					<div
						className="upgradeModalBg"
						onClick={() => setShowReviewModal(false)}
					/>
					{
						/*canReview*/ true ? (
							<Dialog
								open={showReviewModal}
								onOpenChange={setShowReviewModal}>
								<DialogContent className="dialogScrollbar h-[625px] overflow-y-auto sm:max-w-[425px] ">
									<DialogHeader>
										<DialogTitle className="pt-3">
											Reviews
										</DialogTitle>
										<DialogDescription>
											Reviews and Replies
										</DialogDescription>
									</DialogHeader>

									<ReviewForm onCreate={onCreateReview} />

									<ReviewSlide
										profileId={profile?.userId}
										onUpdateReview={onUpdateReview}
										reviews={reviews}
									/>
									<DialogFooter className="md:hidden">
										<DialogClose asChild>
											<Button type="button" variant="secondary">
												Close
											</Button>
										</DialogClose>
									</DialogFooter>
								</DialogContent>
							</Dialog>
						) : (
							<Dialog
								open={showReviewModal}
								onOpenChange={setShowReviewModal}>
								<DialogContent className="dialogScrollbar h-[625px] overflow-y-auto sm:max-w-[425px] ">
									<DialogHeader>
										<DialogTitle className="pt-3">
											Reviews
										</DialogTitle>
										<DialogDescription>
											Reviews and Replies
										</DialogDescription>
									</DialogHeader>
									<ReviewSlide
										profileId={profile?.userId}
										onUpdateReview={onUpdateReview}
										reviews={reviews}
									/>
									<DialogFooter className="md:hidden">
										<DialogClose asChild>
											<Button type="button" variant="secondary">
												Close
											</Button>
										</DialogClose>
									</DialogFooter>
								</DialogContent>
							</Dialog>
						)
					}
				</>
			)}
			{showModal && (
				<>
					<div
						className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-black bg-opacity-40 w-full h-full z-0"
						onClick={() => setShowModal(false)}
					/>
					<div className="fixed top-1/2 left-1/2">
						<div className="container">
							<div className="w-96 flex flex-col items-center bg-white text-black text-center rounded-lg p-8 transform -translate-x-1/2 -translate-y-1/2">
								<button
									className="w-7 text-lg text-gray-400 self-end bg-transparent border-none mb-2"
									onClick={() => setShowModal(false)}>
									✖
								</button>
								<img
									src="/images/bazzarroom-logo-small.png"
									alt="bazaar-logo-img"
									className="w-20 mb-4"
								/>
								<p className="mb-10 text-lg font-semibold">
									Feature Locked
									<h6 className="text-sm font-light text-gray-400">
										You can unlock this feature by signing up
									</h6>
								</p>
								<button
									className="bg-red-500 border-none rounded-md w-48 py-3 text-base text-white shadow-md"
									onClick={() => {
										signIn(undefined, {
											callbackUrl: `/profile/${profile?.userId}`,
										});
									}}>
									Go To Sign Up
								</button>
							</div>
						</div>
					</div>
				</>
			)}

			{isLoading && (
				<div className="w-full h-full absolute rounded-lg">
					<div className="absolute h-200 rounded-lg z-10 m-auto">
						<p className="w-72 text-center absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-9xl rounded-lg">
							<img
								src="/images/loading-red.gif"
								alt=""
								width={30}
								height={30}
							/>
						</p>
					</div>
				</div>
			)}
		</>
	);
}

